import React, { useState, useEffect } from "react";
import { useCart } from "./CartContext";
import { useNavigate, useLocation } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import { jwtDecode } from 'jwt-decode';

const stripePromise = loadStripe(
    "pk_test_51Qhe4xFYsQpDHoxcoyKC3uAYxD0UYXjrniJTfEtPubNKnLv5PGwSvdYZRmllMeRRnDWyj2REL6DoI5P6WbVNeWU500IJ5547pt"
);

const Payment = () => {
    const { cart } = useCart();
    const navigate = useNavigate();
    const location = useLocation();

    const [hasSubscription, setHasSubscription] = useState(false);
    const [trustPolicyBroken, setTrustPolicyBroken] = useState(false);
    const [trustPolicyEnabled, setTrustPolicyEnabled] = useState(true);
    const [agreeTrustPolicy, setAgreeTrustPolicy] = useState(false);
    const [iban, setIban] = useState("");
    const [ibanVerified, setIbanVerified] = useState(false);
    const [depositSelected, setDepositSelected] = useState(false);
    const [deductionAgreementAccepted, setDeductionAgreementAccepted] = useState(false);

    const calculateSubtotal = () => cart.reduce((total, item) => total + item.price * item.quantity, 0).toFixed(2);
    const calculateTotal = () => parseFloat(calculateSubtotal()).toFixed(2);
    const [cardNumber, setCardNumber] = useState("");
    const [expiryDate, setExpiryDate] = useState("");
    const [cvv, setCvv] = useState("");
    const [loading, setLoading] = useState(false);
    const API_URL = process.env.REACT_APP_API_URL;


    useEffect(() => {
        const fetchSubscriptionData = async () => {
            const token = localStorage.getItem("userToken");
            if (!token) return console.warn("No token found in localStorage.");

            try {
                const decodedToken = jwtDecode(token);
                const userId = decodedToken.userId;

                const response = await axios.get(`${API_URL}/api/user/subscription-status?userId=${userId}`);

                setHasSubscription(response.data.isSubscribed);
                setTrustPolicyBroken(response.data.trustPolicyBroken);
                setDepositSelected(response.data.deposit);
                setIbanVerified(response.data.ibanVerified);

            } catch (error) {
                console.error("Error fetching subscription status:", error);
                setHasSubscription(false);
                setTrustPolicyBroken(false);
                setDepositSelected(false);
                setIbanVerified(false);
            }
        };

        fetchSubscriptionData();

    }, [location.state?.isSubscribed, location.state?.depositSelected, location.state?.ibanVerified, stripePromise]);

    const handleDirectCharge = () => navigate("/card-details?paymentType=card");
    const handleSubscriptionPayment = () => navigate("/card-details?paymentType=subscription");
    const handleVerifyIban = () => navigate(`/card-details?paymentType=save-iban&iban=${iban}`);
    const handleDepositPayment = () => navigate(`/card-details?paymentType=save-deposit`);
    const handleConfirmOrder = () => navigate("/success");
    const handleCheckboxChange = () => setDeductionAgreementAccepted(!deductionAgreementAccepted);


    return (
        <div className="payment-page">
            {/* Header */}
            <header className="header">
                <div className="container">
                    <div className="header-content">
                        <div className="header-logo">Lememeprix</div>
                        <div className="header-actions">
                            <div className="cart-badge">
                                <i className="fa-solid fa-cart-shopping"></i>
                                <span className="badge">{cart.length}</span>
                            </div>
                            <img src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-1.jpg" alt="Profile" className="profile-image" />
                        </div>
                    </div>
                </div>
            </header>

            <main className="main-content">
                <div className="container">
                    <section className="payment-hero">
                        <h1 className="hero-title">Paiement Sécurisé</h1>
                        <p className="hero-subtitle">Finalisez votre commande avec notre système de paiement sécurisé</p>
                        <img className="payment-image" src="https://storage.googleapis.com/uxpilot-auth.appspot.com/c55bed6e54-8af2c78591afd8aef3a6.png" alt="secure payment" />
                    </section>

                    <section className="payment-form">
                        {/* Order Summary */}
                        <div className="order-summary">
                            <h2 className="summary-title">Résumé de la commande</h2>
                            {cart.map(item => (
                                <div key={item._id} className="order-item">
                                    <span className="item-name">{item.itemName}</span>
                                    <span>€{item.price} x {item.quantity} = €{(item.price * item.quantity).toFixed(2)}</span>
                                </div>
                            ))}
                            <div className="order-total">
                                <span>Total:</span>
                                <span>€{calculateTotal()}</span>
                            </div>
                        </div>

                        {/* Main Conditional Rendering Block - UI ONLY CHANGES HERE */}
                        {!hasSubscription ? (
                            // User does not have a subscription
                            <div className="payment-options">
                                <button className="pay-direct-button" onClick={handleDirectCharge}>Payer directement</button>
                                <button className="subscribe-button" onClick={handleSubscriptionPayment}>S'abonner (€2.99/mois)</button>
                            </div>
                        ) : trustPolicyBroken ? (
                            // User has a subscription BUT trust policy is broken
                            !ibanVerified && !depositSelected ? (
                                <div className="trust-options">
                                    <button className="verify-iban-button" onClick={handleVerifyIban}>Entrer IBAN</button>
                                    <button className="pay-deposit-button" onClick={handleDepositPayment}>Payer un acompte de 10 €</button>
                                </div>
                            ) : (
                                <div className="trust-policy">
                                    <label>
                                        <input type="checkbox" checked={deductionAgreementAccepted} onChange={handleCheckboxChange} />
                                        J'accepte qu'en cas de non-présentation pour récupérer la commande, le prix de la commande puisse être déduit de mon acompte ou de mon IBAN.
                                    </label>
                                    <button className="confirm-order-button" disabled={!deductionAgreementAccepted} onClick={handleConfirmOrder}>Confirmer la commande</button>
                                </div>
                            )
                        ) : (
                            // User has a subscription AND trust policy is NOT broken
                            <div className="trust-policy">
                                <label>
                                    <input type="checkbox" checked={agreeTrustPolicy} onChange={() => setAgreeTrustPolicy(!agreeTrustPolicy)} />
                                    J'accepte la politique de non-présentation.
                                </label>
                                <button className="confirm-order-button" disabled={!agreeTrustPolicy} onClick={handleConfirmOrder}>Confirmer la commande</button>
                            </div>
                        )}
                    </section>
                </div>
            </main>

            <footer className="footer">
                <div className="container">
                    <div className="footer-content">
                        <div className="footer-section">
                            <h3 className="footer-title">Liens rapides</h3>
                            <ul className="footer-links">
                                <li><span className="footer-link">À propos de nous</span></li>
                                <li><span className="footer-link">Contact</span></li>
                                <li><span className="footer-link">Politique de confidentialité</span></li>
                            </ul>
                        </div>
                        <div className="footer-section">
                            <h3 className="footer-title">Assistance</h3>
                            <p className="contact-info">Besoin d'aide ? Contactez-nous à</p>
                            <p className="contact-info">support@lememeprix.com</p>
                        </div>
                        <div className="footer-section">
                            <h3 className="footer-title">Suivez-nous</h3>
                            <div className="social-icons">
                                <a href="#" className="social-icon">
                                    <i className="fa-brands fa-instagram"></i>
                                </a>
                                <a href="#" className="social-icon">
                                    <i className="fa-brands fa-facebook"></i>
                                </a>
                                <a href="#" className="social-icon">
                                    <i className="fa-brands fa-twitter"></i>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
            <style jsx>{`
                /* Styles for the UI conversion (ONLY) */
               /* Basic page styling */
            .payment-page {
                min-height: 100vh;
                background-color: #f9fafb;
                display: flex;
                flex-direction: column;
            }

            .container {
                max-width: 1200px;
                margin: 0 auto;
                padding: 0 1rem;
            }

            /* Header */
            .header {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                background-color: #fff;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                z-index: 50;
            }

            .header .container {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 1rem;
            }

            .header-content {
                display: flex;
                justify-content: space-between;
                align-items: center;
                width: 100%;
            }

            .header-logo {
                font-size: 1.5rem;
                font-weight: bold;
            }

                .header-actions {
                      display: flex;
                    align-items: center;
                     gap: 1rem;
                                     }

                .cart-badge {
                    position: relative;
                    cursor: pointer;
                }

                .cart-badge i {
                    font-size: 1.5rem;
                }

                .badge {
                    position: absolute;
                    top: -0.5rem;
                    right: -0.5rem;
                    background-color: #ef4444;
                    color: #fff;
                    font-size: 0.75rem;
                    width: 1.25rem;
                    height: 1.25rem;
                    border-radius: 9999px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                /* Styling for header profile image */
                .profile-image {
                    width: 3rem;
                    height: 3rem;
                    border-radius: 9999px;
                    object-fit: cover;
                }

                /* Main Content */
                .main-content {
                    padding-top: 5rem;
                }

                /* Hero Section */
                .payment-hero {
                    padding: 4rem 0;
                    text-align: center;
                }

                .hero-title {
                    font-size: 2.25rem;
                    font-weight: bold;
                    margin-bottom: 1rem;
                    color: #374151;
                }

                .hero-subtitle {
                    font-size: 1.125rem;
                    color: #6b7280;
                    margin-bottom: 2rem;
                }

                .payment-image {
                    width: 24rem;
                    height: auto;
                    margin: 0 auto;
                }

                /* Payment Form Section */
                .payment-form {
                    max-width: 40rem;
                    margin: 2rem auto;
                    background-color: #fff;
                    border-radius: 0.75rem;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                    padding: 2rem;
                }
                 .order-summary {
                    margin-bottom: 2rem;
                         padding: 1rem;
                         border: 1px solid #e5e7eb;
                         border-radius: 0.5rem;
                 }

                   .order-summary h2 {
                       font-size: 1.25rem;
                           font-weight: bold;
                            margin-bottom: 1rem;
                              }
            .order-item {
             display: flex;
                                justify-content: space-between;
                    margin-bottom: 0.5rem;
                                }

                .order-total {
                         font-weight: bold;
                           padding-top: 1rem;
                             border-top: 1px solid #e5e7eb;
                         }

                           .trust-policy {
                            margin-bottom: 1.5rem;
                            padding: 1rem;
                            background-color: #f9fafb;
                            border-radius: 0.5rem;
        }

        .trust-policy label {
           display: block;
                font-size: 0.875rem;
                     color: #6b7280;
           }

        /* Button Styles - Updated for consistency */
        .pay-direct-button,
        .subscribe-button,
        .verify-iban-button,
        .pay-deposit-button,
        .confirm-order-button {
            background-color: #ef4444;
            color: white;
            padding: 0.75rem 1.25rem;
            border-radius: 0.5rem;
            text-decoration: none;
            font-weight: 500;
            cursor: pointer;
            transition: background-color 0.2s ease-in-out;
            border: none;
            display: inline-block;
            margin-right: 0.5rem;
        }

        .pay-direct-button:hover,
        .subscribe-button:hover,
        .verify-iban-button:hover,
        .pay-deposit-button:hover,
        .confirm-order-button:hover {
            background-color: #dc2626;
        }

        /* New styles for responsive adjustments */
        @media (max-width: 768px) {
            .main-content {
                padding-top: 4rem; /* Slightly reduce padding on smaller screens */
            }

            .payment-form {
                margin: 1rem; /* Smaller margins on smaller screens */
                padding: 1rem; /* Reduced padding */
            }
        }
                    /* Footer */
                   .footer {
                   background-color: #f9fafb;
                      padding: 3rem 0;
                      margin-top: 4rem;
               }

         .footer .container {
              max-width: 1200px;
                  margin: 0 auto;
                    padding: 0 1rem;
                     }

                       .footer-content {
                       display: grid;
                           grid-template-columns: repeat(1, 1fr);
                           gap: 2rem;
            }

               @media (min-width: 768px) {
                      .footer-content {
                          grid-template-columns: repeat(3, 1fr);
                  }
        }

            .footer-section {
                    display: flex;
                       flex-direction: column;
                       }

        .footer-title {
               font-size: 1.25rem;
                      font-weight: bold;
                     margin-bottom: 1rem;
                }

               .footer-links {
                     list-style: none;
                       padding: 0;
                  }

           .footer-links li {
            margin-bottom: 0.5rem;
                  }

               .footer-link {
              color: #4b5563;
                      cursor: pointer;
                       transition: color 0.2s ease;
                        text-decoration: none;
                         }

               .footer-link:hover {
                color: #6b7280;
                 }

            .social-icons {
           display: flex;
                     gap: 1rem;
                     }

          .social-icons i {
            font-size: 1.5rem;
                    color: #4b5563;
                      cursor: pointer;
                      transition: color 0.2s ease;
                  }

           .social-icons i:hover {
                color: #6b7280;
                       }

            .contact-info {
              display: flex;
                   flex-direction: column;
                }

            .contact-details {
                    display: flex;
                    align-items: center;
               gap: 0.5rem;
                         margin-bottom: 0.5rem;
                        color: #4b5563;
                  }
            `}</style>
        </div>
    );
};

export default Payment;